import React, { useEffect, useRef, useState } from 'react';
import { FormField } from '../ui/FormField';
import { SlidingSwitchButton } from '../ui/Buttons';
import { OptionsSelectInput } from '../inputs/OptionsSelectInput';
import { createCategory, getCategory, updateCategory } from '../../services/category/category.service';
import type { CategoryAssociatedSite, CategorySummary } from '../../services/category/category.types';
import { getCustomerSites, listCustomers } from '../../services/customer/customer.service';
import type { CustomerListItem, CustomerSiteSummary } from '../../services/customer/customer.types';
import { assignSiteCategory, removeSiteCategory } from '../../services/siteManagement/siteManagement.service';
import { getCategoryErrorMessage, getSiteRegistryId } from '../../features/categories/categoryCustomers';

interface CategoryFormData {
  name: string;
  description: string;
  isActive: boolean;
  associatedSites: CategoryAssociatedSite[];
}

interface CategoryFormProps {
  onSubmit?: (category: CategorySummary, warning?: string) => void | Promise<void>;
  onCancel?: () => void;
  initialData?: Partial<CategoryFormData>;
  isLoading?: boolean;
  categoryId?: number;
  onSubmitting?: (submitting: boolean) => void;
  formRef?: React.RefObject<HTMLFormElement | null>;
}

const NAME_REQUIRED_MESSAGE = 'Name is required';
const NAME_MIN_LENGTH_MESSAGE = 'Name must be at least 2 characters';
const NAME_MAX_LENGTH_MESSAGE = 'Name must be less than 100 characters';
const DESCRIPTION_REQUIRED_MESSAGE = 'Description is required';
const DESCRIPTION_MAX_LENGTH_MESSAGE = 'Description must be less than 250 characters';

export const CategoryForm: React.FC<CategoryFormProps> = ({
  onSubmit,
  onCancel,
  initialData = {},
  isLoading = false,
  categoryId,
  onSubmitting,
  formRef,
}) => {
  const [name, setName] = useState<string>(initialData?.name ?? '');
  const [description, setDescription] = useState<string>(initialData?.description ?? '');
  const [isActive, setIsActive] = useState<boolean>(initialData?.isActive ?? true);
  const [customers, setCustomers] = useState<CustomerListItem[]>([]);
  const [selectedCustomerIds, setSelectedCustomerIds] = useState<number[]>([]);
  const [sites, setSites] = useState<CustomerSiteSummary[]>([]);
  const [siteCustomerIds, setSiteCustomerIds] = useState<Record<number, number>>({});
  const [selectedSiteIds, setSelectedSiteIds] = useState<number[]>(
    initialData.associatedSites?.map(site => site.siteIdentifierRegistryId) ?? []
  );
  const [loadingCustomers, setLoadingCustomers] = useState(true);
  const [loadingSites, setLoadingSites] = useState(false);
  const [selectionError, setSelectionError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nameError, setNameError] = useState<string | null>(null);
  const [descriptionError, setDescriptionError] = useState<string | null>(null);
  const restoredSelectionsRef = useRef(false);
  const siteLoadRequestRef = useRef(0);

  useEffect(() => {
    setName(initialData?.name ?? '');
    setDescription(initialData?.description ?? '');
    setIsActive(initialData?.isActive ?? true);
    setSelectedSiteIds(initialData.associatedSites?.map(site => site.siteIdentifierRegistryId) ?? []);
  }, [initialData?.name, initialData?.description, initialData?.isActive, initialData.associatedSites]);

  useEffect(() => {
    const loadCustomers = async () => {
      setLoadingCustomers(true);
      try {
        const response = await listCustomers();
        if (!response.success) {
          setSelectionError(response.error?.message || 'Failed to load customers');
          return;
        }
        setCustomers(response.data ?? []);
      } catch (err) {
        setSelectionError(err instanceof Error ? err.message : 'Failed to load customers');
      } finally {
        setLoadingCustomers(false);
      }
    };

    void loadCustomers();
  }, []);

  useEffect(() => {
    const savedSiteIds = initialData.associatedSites?.map(site => site.siteIdentifierRegistryId) ?? [];
    if (!categoryId || customers.length === 0 || savedSiteIds.length === 0 || restoredSelectionsRef.current) {
      return;
    }

    const restoreSelections = async () => {
      restoredSelectionsRef.current = true;
      setLoadingSites(true);
      try {
        const responses = await Promise.all(customers.map(async customer => ({
          customerId: customer.id,
          response: await getCustomerSites(customer.id),
        })));
        const savedSiteIdSet = new Set(savedSiteIds);
        const matchingSites: CustomerSiteSummary[] = [];
        const matchingCustomerIds = new Set<number>();
        const customerIdsBySite: Record<number, number> = {};

        responses.forEach(({ customerId, response }) => {
          if (!response.success) {
            throw new Error(response.error?.message || 'Failed to restore category sites');
          }
          (response.data ?? []).forEach(site => {
            const siteRegistryId = getSiteRegistryId(site);
            customerIdsBySite[siteRegistryId] = customerId;
            if (savedSiteIdSet.has(siteRegistryId)) {
              matchingCustomerIds.add(customerId);
              matchingSites.push(site);
            }
          });
        });

        setSiteCustomerIds(customerIdsBySite);
        setSites(matchingSites);
        setSelectedCustomerIds([...matchingCustomerIds]);
      } catch (err) {
        setSelectionError(err instanceof Error ? err.message : 'Failed to restore category sites');
      } finally {
        setLoadingSites(false);
      }
    };

    void restoreSelections();
  }, [categoryId, customers, initialData.associatedSites]);

  useEffect(() => {
    const loadSites = async () => {
      const requestId = ++siteLoadRequestRef.current;
      if (selectedCustomerIds.length === 0) {
        setSites([]);
        setLoadingSites(false);
        return;
      }

      setLoadingSites(true);
      setSelectionError(null);
      try {
        const responses = await Promise.all(selectedCustomerIds.map(getCustomerSites));
        const sitesById = new Map<number, CustomerSiteSummary>();
        const customerIdsBySite: Record<number, number> = {};
        responses.forEach((response, index) => {
          if (!response.success) {
            throw new Error(response.error?.message || 'Failed to load sites');
          }
          (response.data ?? []).forEach(site => {
            const siteRegistryId = getSiteRegistryId(site);
            sitesById.set(siteRegistryId, site);
            customerIdsBySite[siteRegistryId] = selectedCustomerIds[index];
          });
        });
        if (requestId !== siteLoadRequestRef.current) {
          return;
        }
        setSiteCustomerIds(customerIdsBySite);
        setSites([...sitesById.values()]);
      } catch (err) {
        if (requestId !== siteLoadRequestRef.current) {
          return;
        }
        setSelectionError(err instanceof Error ? err.message : 'Failed to load sites');
        setSites([]);
      } finally {
        if (requestId === siteLoadRequestRef.current) {
          setLoadingSites(false);
        }
      }
    };

    void loadSites();
  }, [selectedCustomerIds]);

  const validate = (): boolean => {
    let isValid = true;

    const trimmedName = name.trim();
    if (!trimmedName) {
      setNameError(NAME_REQUIRED_MESSAGE);
      isValid = false;
    } else if (trimmedName.length < 2) {
      setNameError(NAME_MIN_LENGTH_MESSAGE);
      isValid = false;
    } else if (trimmedName.length > 100) {
      setNameError(NAME_MAX_LENGTH_MESSAGE);
      isValid = false;
    } else {
      setNameError(null);
    }

    const trimmedDescription = description.trim();
    if (!trimmedDescription) {
      setDescriptionError(DESCRIPTION_REQUIRED_MESSAGE);
      isValid = false;
    } else if (trimmedDescription.length > 250) {
      setDescriptionError(DESCRIPTION_MAX_LENGTH_MESSAGE);
      isValid = false;
    } else {
      setDescriptionError(null);
    }

    return isValid;
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setName(value);

    const trimmed = value.trim();
    if (trimmed && trimmed.length >= 2 && trimmed.length <= 100) {
      setNameError(null);
    }
  };

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setDescription(value);

    const trimmed = value.trim();
    if (trimmed && trimmed.length <= 250) {
      setDescriptionError(null);
    }
  };

  const handleCustomerSelectionChange = (values: Array<string | number>) => {
    if (submitting || isLoading) return;
    const customerIds = values.map(Number);
    const selectedCustomerIdSet = new Set(customerIds);
    setSelectedCustomerIds(customerIds);
    setSelectedSiteIds(previousSiteIds => previousSiteIds.filter(siteId => {
      const customerId = siteCustomerIds[siteId];
      return customerId === undefined || selectedCustomerIdSet.has(customerId);
    }));
    setSelectionError(null);
  };

  const handleSiteSelectionChange = (values: Array<string | number>) => {
    if (submitting || isLoading) return;
    setSelectedSiteIds(values.map(Number));
  };

  const getSelectedAssociatedSites = (): CategoryAssociatedSite[] => selectedSiteIds.map(siteId => {
    const site = sites.find(currentSite => getSiteRegistryId(currentSite) === siteId);
    const siteName = site?.siteName || site?.displayName || site?.siteIdentifier || String(siteId);
    return {
      siteIdentifierRegistryId: siteId,
      siteIdentifier: site?.siteIdentifier || String(siteId),
      siteName,
    };
  });

  const updateSiteAssignments = async (savedCategoryId: number) => {
    const previousSiteIds = initialData.associatedSites?.map(site => site.siteIdentifierRegistryId) ?? [];
    const previousSiteIdSet = new Set(previousSiteIds);
    const selectedSiteIdSet = new Set(selectedSiteIds);
    const addedSiteIds = selectedSiteIds.filter(siteId => !previousSiteIdSet.has(siteId));
    const removedSiteIds = previousSiteIds.filter(siteId => !selectedSiteIdSet.has(siteId));
    const operations = [
      ...addedSiteIds.map(siteId => () => assignSiteCategory(siteId, savedCategoryId)),
      ...removedSiteIds.map(siteId => () => removeSiteCategory(siteId)),
    ];
    const failures = await Promise.all(operations.map(async operation => {
      try {
        const response = await operation();
        return response.success ? null : response.error?.message || 'Failed to update site category assignments';
      } catch (err: unknown) {
        return getCategoryErrorMessage(err, 'Failed to update site category assignments');
      }
    }));
    const messages = failures.filter((message): message is string => message !== null);
    if (messages.length) throw new Error(messages.join('\n'));
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (submitting || isLoading) return;
    setError(null);

    if (loadingCustomers || loadingSites || selectionError) {
      setError(selectionError || 'Please wait for customer and site options to finish loading');
      return;
    }
    if (!validate()) {
      return;
    }

    setSubmitting(true);
    onSubmitting?.(true);
    try {
      const payload = categoryId
        ? { name: name.trim(), description: description.trim(), isActive }
        : { name: name.trim(), description: description.trim() };
      const res = categoryId
        ? await updateCategory(categoryId, payload)
        : await createCategory(payload);

      if (!res?.success || !res.data) {
        setError(res?.error?.message || 'Failed to save category');
        return;
      }

      const failures: string[] = [];
      let savedCategory = res.data;
      try {
        await updateSiteAssignments(res.data.id);
        const selectedAssociatedSites = getSelectedAssociatedSites();
        savedCategory = {
          ...res.data,
          associatedSiteCount: selectedAssociatedSites.length,
          associatedSites: selectedAssociatedSites,
        };
      } catch (err: unknown) {
        failures.push(getCategoryErrorMessage(err, 'Failed to update site category assignments'));
      }

      if (categoryId || failures.length) {
        try {
          const detailResponse = await getCategory(res.data.id);
          if (!detailResponse.success || !detailResponse.data) {
            throw new Error(detailResponse.error?.message || 'Failed to reload saved category details');
          }
          savedCategory = { ...savedCategory, ...detailResponse.data };
        } catch (err: unknown) {
          failures.push(getCategoryErrorMessage(err, 'Failed to reload saved category details'));
        }
      }

      await onSubmit?.(savedCategory, failures.length ? failures.join('\n') : undefined);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setSubmitting(false);
      onSubmitting?.(false);
    }
  };

  return (
    <form ref={formRef} onSubmit={handleSubmit}>
      <fieldset disabled={submitting || isLoading} style={{ border: 0, margin: 0, padding: 0, minWidth: 0 }}>
      {error && <p className="text-danger">{error}</p>}

      <FormField
        label="Category Name"
        name="name"
        value={name}
        onChange={handleNameChange}
        error={nameError}
        placeholder="Enter category name"
        disabled={submitting || isLoading}
      />

      <FormField
        label="Description"
        name="description"
        value={description}
        onChange={handleDescriptionChange}
        error={descriptionError}
        placeholder="Enter category description"
        disabled={submitting || isLoading}
      />

      <div className="form__group">
        <label className="form__label">Customers</label>
        {loadingCustomers ? (
          <p>Loading customers...</p>
        ) : (
          <OptionsSelectInput
            options={customers.map(customer => ({ id: customer.id, label: customer.customerName }))}
            selected={selectedCustomerIds}
            onChange={handleCustomerSelectionChange}
            placeholder="Select customer(s)..."
          />
        )}
      </div>

      {selectedCustomerIds.length > 0 && (
        <div className="form__group">
          <label className="form__label">Sites</label>
          {loadingSites ? (
            <p>Loading sites...</p>
          ) : (
            <OptionsSelectInput
              options={sites.map(site => ({
                id: getSiteRegistryId(site),
                label: site.siteName || site.displayName || site.siteIdentifier || String(getSiteRegistryId(site)),
              }))}
              selected={selectedSiteIds}
              onChange={handleSiteSelectionChange}
              placeholder="Select site(s)..."
              showChipLabelTooltip
            />
          )}
        </div>
      )}

      {selectionError && <p className="text-danger">{selectionError}</p>}

      {!!categoryId && (
        <div className="form-field form-field--responsive">
          <label htmlFor="isActive" className="form-field__label">Active</label>
          <SlidingSwitchButton
            checked={isActive}
            disabled={submitting || isLoading}
            onChange={setIsActive}
            ariaLabel={`Toggle active status for ${name || 'category'}`}
          />
        </div>
      )}
      </fieldset>
    </form>
  );
};

export default CategoryForm;
